import { useTranslation } from "react-i18next";

import { Form } from "@/components/ui/form";
import { reportTypes } from "@/common/report.common";
import { MezzaTagsField } from "@/components/core/Form/MezzaTagsField";
import { MezzaSelectField } from "@/components/core/Form/MezzaSelectField";
import { LocationMultiSelect } from "@/components/core/Form/LocationMultiSelect";
import { useReportForm } from "@/hooks/useReportForm";
import {
  GenerateReportFormValues,
  ReportErrorKey,
  REPORT_ERROR_MESSAGES,
} from "@/types/report.types";
import { ReportDateRangeFields } from "./ReportDateRangeFields";
import { toast } from "@/utils/toast.utils";
import { useGenerateReportMutation } from "@/hooks/useGenerateReportMutation";
import { ButtonWithLoading } from "@/components/core/Form/ButtonWithLoading";

export function GenerateReportForm() {
  const { t } = useTranslation();
  const { mutate: generateReport, isPending: isLoading } =
    useGenerateReportMutation();

  const onSubmit = (values: GenerateReportFormValues) => {
    generateReport(values, {
      onSuccess: () => {
        const successMessage = t("generateReport.generateReportSuccess");
        toast.success(successMessage);
      },
      onError: (error) => {
        let errorKey = ReportErrorKey.GENERAL_ERROR;

        if (error instanceof Error) {
          const errorMessage = error.message;
          const isKnownError = Object.values(ReportErrorKey).includes(
            errorMessage as ReportErrorKey
          );

          if (isKnownError) {
            errorKey = errorMessage as ReportErrorKey;
          }
        }

        const translationKey = REPORT_ERROR_MESSAGES[errorKey];
        toast.error(t(translationKey));
      },
    });
  };

  const { form, handleFormSubmit } = useReportForm(onSubmit);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFormSubmit)}
        className="gap-6 pb-6 px-1 flex flex-col flex-1 overflow-auto"
      >
        {/* Report Type */}
        <MezzaSelectField
          form={form}
          name="reportType"
          label="generateReport.reportType"
          placeholder="generateReport.reportTypePlaceholder"
          options={reportTypes}
          disabled={isLoading}
        />

        {/* Locations */}
        <LocationMultiSelect
          form={form}
          name="locationIds"
          label="generateReport.locations"
          disabled={isLoading}
        />

        {/* Email To */}
        <MezzaTagsField
          form={form}
          name="emailTo"
          label="generateReport.emailTo"
          placeholder="generateReport.emailToPlaceholder"
          disabled={isLoading}
        />

        <ReportDateRangeFields form={form} disabled={isLoading} className="px-4" />

        {/* Submit Button */}
        <ButtonWithLoading
          type="submit"
          className="w-full !mt-auto align-self-end"
          disabled={isLoading}
          isLoading={isLoading}
        >
          {t("generateReport.submit")}
        </ButtonWithLoading>
      </form>
    </Form>
  );
}
